/* eslint-disable @typescript-eslint/no-unused-vars */
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { z } from 'zod/mini'

import { createBlockSchema } from '../schemas/create-block-schema'
import { type CouponApiProps, CouponApiPropsSchema } from '../shared/coupon'
import type { Equal, ExpectTrue } from '../types/validation'

import type { _ApiBlock } from './block'
import { type CTALink, CTALinkSchema } from './cta-link'
import { type CmsIcon, CmsIconSchema } from './icon'
import { type Image, ImageSchema } from './image'
import { RecommendationsApiPropsSchema } from './recommendations'
import { RefinedProductsApiPropsSchema } from './refined-products'

/* TypeScript */
type EnhancedCarouselItem = {
  id: string
  thumbnail?: Maybe<Image>
  thumbnailName?: Maybe<string>
  recommendation: _ApiBlock<'RefinedProducts'>
  saleEndDate?: Maybe<string>
  icon?: Maybe<CmsIcon>
}

export type EnhancedCarouselApiProps = {
  technicalName?: Maybe<string>
  title?: Maybe<string>
  subtitle?: Maybe<string>
  image?: Maybe<Image>
  carousels: Array<EnhancedCarouselItem>
  cta?: Maybe<CTALink>
  coupon?: Maybe<CouponApiProps>
  saleEndDate?: Maybe<string>
  theme?: Maybe<'light' | 'dark'>
}

/* Schemas */
const EnhancedCarouselItemSchema = z.object({
  id: z.string(),
  thumbnail: z.nullish(ImageSchema),
  thumbnailName: z.nullish(z.string()),
  recommendation: z.union([
    createBlockSchema('RefinedProducts', RefinedProductsApiPropsSchema),
  ]),
  saleEndDate: z.nullish(z.iso.datetime()),
  icon: z.nullish(CmsIconSchema),
})

export const EnhancedCarouselApiPropsSchema = z.object({
  technicalName: z.nullish(z.string()),
  title: z.nullish(z.string()),
  subtitle: z.nullish(z.string()),
  image: z.nullish(ImageSchema),
  carousels: z.array(EnhancedCarouselItemSchema),
  cta: z.nullish(CTALinkSchema),
  coupon: z.nullish(CouponApiPropsSchema),
  saleEndDate: z.nullish(z.iso.datetime()),
  theme: z.nullish(z.enum(['light', 'dark'])),
})

/* Schemas validation */
type TestEnhancedCarouselProps = ExpectTrue<
  Equal<
    EnhancedCarouselApiProps,
    z.infer<typeof EnhancedCarouselApiPropsSchema>
  >
>
